import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import {
  FormControl,
  Typography,
  Select,
  MenuItem,
  Switch,
  Button,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import moment from "moment-timezone";
import { v4 as uuidv4 } from "uuid";
import {
  fetchData,
  postQuery,
} from "../../Requirement/services/RequirementServices";
import AlertModal from "../../Requirement/components/common/AlerModel";

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  display: "flex",
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transition: "transform 300ms ease-in-out",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#de6b14",
        opacity: 1,
        border: 0,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#33cf4d",
      border: "6px solid #fff",
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 22,
    height: 22,
    transition: "all 300ms ease-in-out",
  },
  "& .MuiSwitch-track": {
    borderRadius: 13,
    backgroundColor: "#ccc",
    opacity: 1,
    transition: "background-color 300ms ease-in-out",
  },
}));

const SellerFarmerLanguageRegion = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    preferredLanguage: "",
    regionCountry: "",
    currencyPreference: "",
    timeZone: "",
    timeFormat: "24-hour",
  });
  const [errors, setErrors] = useState({});
  const [is24Hour, setIs24Hour] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeUser, setActiveUser] = useState("");
  const [countrymaster, setCountryMaster] = useState([]);
  const [timeZones, setTimeZones] = useState([]);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });
  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "Value",
          rowStart: 0,
          pageSize: 1000,
          filterTerms: { Type: "Country" },
        }),
      ]);
      setCountryMaster(allCountries.items.length > 0 ? allCountries.items : []);
    } catch (error) {
      console.error("Error loading countries: ", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTimeZones = useCallback((country) => {
    const countryCodeMap = {
      India: "IN",
      Afghanistan: "AF",
      Australia: "AU",
      Bangladesh: "BD",
      Brazil: "BR",
      Oman: "OM",
      Nigeria: "NG",
      Belgium: "BE",
      Germany: "DE",
      Belize: "BZ",
      Barbados: "BB",
      "Saudi Arabia": "SA",
      Russia: "RU",
      Vietnam: "VN",
      Qatar: "QA",
      "United States of America": "US",
      "United Kingdom": "GB",
      "United Arab Emirates": "AE",
    };
    const countryCode = countryCodeMap[country] || country;
    const zones = moment.tz.zonesForCountry(countryCode, true);
    if (zones && zones.length > 0)
      setTimeZones(
        zones.map((zone) => ({
          name: zone.name,
          offset: `GMT${moment.tz(zone.name).format("Z")}`,
        }))
      );
    else setTimeZones([]);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "regionCountry") {
      setFormData((prev) => ({ ...prev, timeZone: "" }));
      loadTimeZones(value);
    }
  };

  const handleToggle = (e) => {
    const isChecked = e.target.checked;
    setIs24Hour(isChecked);
    setFormData((prev) => ({
      ...prev,
      timeFormat: isChecked ? "12-hour" : "24-hour",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userSettings = await fetchData({
        target: "listUserSettings",
        userOrder: "SettingID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { EmailID: activeUser },
      });
      const userSettingsInput = {
        EmailID: activeUser,
        PreferredLanguage: formData.preferredLanguage,
        RegionCountry: formData.regionCountry,
        CurrencyPreference: formData.currencyPreference,
        TimeZone: formData.timeZone,
        TimeFormat: formData.timeFormat,
      };
      if (userSettings.items && userSettings.items.length > 0) {
        const settingID = userSettings.items[0].SettingID;
        await postQuery({
          target: "updateUserSettings",
          userSettingsInput: { ...userSettingsInput, SettingID: settingID },
        });
      } else {
        const settingID = uuidv4();
        await postQuery({
          target: "createUserSettings",
          userSettingsInput: { ...userSettingsInput, SettingID: settingID },
        });
      }
      setSubmitted(true);
      setModal({
        show: true,
        type: "success",
        title: "Success!",
        message: "Language & Region settings updated successfully.",
        autoClose: false,
      });
    } catch (error) {
      console.error("Error submitting settings:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: error.message || "Failed to save settings. Please try again.",
        autoClose: false,
      });
      setErrors({ submit: "Failed to save settings. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_SELLERS_AND_FARMERS")) {
          await Auth.signOut();
          navigate("/login/seller-farmer");
        } else {
          const email = user.attributes.email;
          setActiveUser(email);
          try {
            const userSettings = await fetchData({
              target: "listUserSettings",
              userOrder: "SettingID",
              rowStart: 0,
              pageSize: 1,
              filterTerms: { EmailID: email },
            });
            if (userSettings.items && userSettings.items.length > 0) {
              const settings = userSettings.items[0];
              setFormData({
                preferredLanguage: settings.PreferredLanguage || "",
                regionCountry: settings.RegionCountry || "",
                currencyPreference: settings.CurrencyPreference || "",
                timeZone: settings.TimeZone || "",
                timeFormat: settings.TimeFormat || "24-hour",
              });
              setIs24Hour(settings.TimeFormat === "12-hour");
              if (settings.RegionCountry) loadTimeZones(settings.RegionCountry);
            }
          } catch (error) {
            console.error("Error loading user settings:", error);
          }
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/seller-farmer");
      }
    };
    checkUser();
  }, [navigate, loadTimeZones]);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  return (
    <div className="container mt-4">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card p-2">
        <form className="row g-2" id="questionForm" onSubmit={handleSubmit}>
          <div className="table-header">
            <div className="header-text">
              <div className="head-text">Language & Region Settings</div>
              <div className="head-subtext">
                Customize your language, region, currency, and time preferences.
              </div>
            </div>
            <div className="head-btn">
              <div className="submit-btn-form">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  form="questionForm"
                  disabled={loading}
                >
                  {loading
                    ? "Submitting..."
                    : submitted
                    ? "Submitted"
                    : "Submit"}
                </Button>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <FormControl fullWidth>
              <Typography variant="body2">Preferred Language</Typography>
              <Select
                name="preferredLanguage"
                value={formData.preferredLanguage}
                onChange={handleChange}
                displayEmpty
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="" disabled>
                  Select language
                </MenuItem>
                <MenuItem value="English">English</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="col-md-6">
            <FormControl fullWidth>
              <Typography variant="body2">Region / Country</Typography>
              <Select
                name="regionCountry"
                value={formData.regionCountry}
                onChange={handleChange}
                displayEmpty
                sx={{ backgroundColor: "white" }}
                error={!!errors.regionCountry}
              >
                <MenuItem value="" disabled>
                  Select country
                </MenuItem>
                {countrymaster.map((p) => (
                  <MenuItem key={p.ID} value={p.Value}>
                    {p.Value}
                  </MenuItem>
                ))}
              </Select>
              {errors.regionCountry && (
                <Typography variant="caption" color="error">
                  {errors.regionCountry}
                </Typography>
              )}
            </FormControl>
          </div>
          <div className="col-md-6">
            <FormControl fullWidth>
              <Typography variant="body2">Currency Preference</Typography>
              <Select
                name="currencyPreference"
                value={formData.currencyPreference}
                onChange={handleChange}
                displayEmpty
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="" disabled>
                  Select currency
                </MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="col-md-6">
            <FormControl fullWidth>
              <Typography variant="body2">Time Zone</Typography>
              <Select
                name="timeZone"
                value={formData.timeZone}
                onChange={handleChange}
                displayEmpty
                sx={{ backgroundColor: "white" }}
                disabled={!formData.regionCountry}
              >
                <MenuItem value="" disabled>
                  Select time zone
                </MenuItem>
                {timeZones.map((tz) => (
                  <MenuItem
                    key={tz.name}
                    value={tz.name}
                  >{`${tz.name} (${tz.offset})`}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="col-md-12">
            <div
              className="flex-center"
              style={{
                display: "flex",
                boxShadow: "1px 1px 5px #ccc",
                padding: "20px",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body2">Time Format</Typography>
              <FormControlLabel
                control={
                  <IOSSwitch
                    checked={is24Hour}
                    onChange={handleToggle}
                    name="timeFormat"
                    sx={{ m: 1 }}
                  />
                }
                label=""
                sx={{ marginRight: 1 }}
              />
              <Typography variant="body2">
                {is24Hour ? "12-hour format" : "24-hour format"}
              </Typography>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SellerFarmerLanguageRegion;
